import { MeQueryQuery, UserType } from "../generated/graphql";
import { LEARNING_VIEWER_QUERY, ME_QUERY } from "../graphql/queries";
import { client } from "../lib/apolloClient";
import { getAuthData } from "../lib/authStorage";
import { captureException } from "../lib/errorHandling";
import { formatName } from "../lib/formatName";

export interface UserIdentity {
  id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  kind: UserType;
  organizationId?: string;
  organizationName?: string;
  hasLearningContent?: boolean;
}

type LearningViewerQuery = {
  learningViewer?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    organizationId: string;
  } | null;
};

const getLearningViewerIdentity = async (): Promise<UserIdentity> => {
  const { data } = await client.query<LearningViewerQuery>({
    query: LEARNING_VIEWER_QUERY,
    fetchPolicy: "network-only",
  });
  if (!data.learningViewer) throw new Error("Learning viewer not found");

  const { id, firstName, lastName, organizationId } = data.learningViewer;
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  return {
    id,
    fullName: fullName || getAuthData()?.email || id,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    kind: UserType.Caregiver,
    organizationId,
  };
};

// Helper function to get user identity using Apollo Client
export const getCurrentUserIdentity = async (): Promise<UserIdentity> => {
  const auth = getAuthData();
  if (!auth) throw new Error("Not authenticated");

  try {
    if (auth.surface === "learning") return await getLearningViewerIdentity();

    const { data } = await client.query<MeQueryQuery>({
      query: ME_QUERY,
      fetchPolicy: "network-only", // Always fetch fresh data for auth
    });

    if (!data?.me) {
      throw new Error("User not found");
    }

    const user = data.me;

    return {
      id: user.id,
      fullName: formatName(user),
      firstName: user.firstName || undefined,
      lastName: user.lastName || undefined,
      email: user.email || undefined,
      kind: user.kind,
      organizationId: user.organizationManager?.organizationId || undefined,
      organizationName: user.organizationManager?.organization?.name || undefined,
      hasLearningContent: data.hasLearningContent ?? false,
    };
  } catch (error) {
    captureException(error, "auth/identity: fetch current user");
    throw new Error("Failed to fetch user data");
  }
};
